import cloneDeep from 'lodash.clonedeep';
import { createNode } from '@glueapp/graph-types';
import { isShapeValue } from '@glueapp/graph-types';

export const ObjectPrimitive = createNode({
    id: 'Object',
    name: 'Object',
    description: 'Create an Object',
    aiDescription: [
        'Creates a typed object from individual property inputs.',
        'Setup:',
        '1. Add the node: glue add Object',
        '2. Set the "Object Shape" port value to define the object structure:',
        '   glue set-value <id> "Object Shape" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"name":{"kind":"string"},"age":{"kind":"number"}},"restriction":"open"}}\'',
        '3. Run glue shape <id> to see the generated input ports (one per property) and the "Created Object" output port.',
        '4. Connect values to each property input port.',
        '',
        'The "Object Shape" port accepts a shape value with an object type. Setting it triggers computeShape which:',
        '- Creates one input port per property defined in the shape (typed to match each property).',
        '- Creates a "Created Object" output port typed to the full object shape.',
        'Without a shape value set, there are no output ports.',
        '',
        'Available type kinds for properties: string, number, boolean, object, array, null, union, optional.',
        'Array example: {"kind":"array","subType":{"kind":"string"}}',
        'Optional properties: add "optionalProperties":["fieldName"] to the object type.',
        '',
        'Example - create a user object:',
        '  glue add Object  # returns <id>',
        '  glue set-value <id> "Object Shape" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"username":{"kind":"string"},"email":{"kind":"string"},"active":{"kind":"boolean"}},"restriction":"open"}}\'',
        '  glue shape <id>  # shows ports: username (string), email (string), active (boolean), Created Object (object)',
        '  glue connect <string-node>:Output <id>:username',
    ].join('\n'),
    category: 'Object',
    color: '#a17ee2',
    integration: undefined,
    computeShape: input => {
        const shapePortValue = input.portValues['Object Shape'];
        if (isShapeValue(shapePortValue)) {
            const shapePortValueType = shapePortValue.type;
            if (shapePortValueType.kind !== 'object') {
                throw new Error('Object Shape must be an object');
            }

            return {
                inPorts: [
                    input.nodeInPorts[0],
                    ...Object.entries(shapePortValueType.properties).map(([key, type]) => ({
                        name: key,
                        type,
                        optional: shapePortValueType.optionalProperties?.includes(key) ?? false,
                    })),
                ],
                outPorts: [{ name: 'Created Object', type: cloneDeep(shapePortValue.type) }],
            };
        } else {
            return { inPorts: input.nodeInPorts, outPorts: [] };
        }
    },
    inPorts: {
        'Object Shape': {
            kind: 'shape',
            restriction: {
                kind: 'object',
                properties: {},
                restriction: 'open',
            },
        },
    },
    outPorts: {},
    execute: function (input) {
        const { 'Object Shape': shape, ...remainingInPorts } = input.inPorts;

        return { output: { 'Created Object': { ...remainingInPorts } } };
    },
});
